import { useAppDispatch } from '@customer/store/hooks';
import { Ref, useCallback, useRef } from 'react';
import { Action } from '@reduxjs/toolkit';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';

interface ReturnControlProps<T> {
  // TODO: After upgrading to React 19, replace elementRef with ref since forwardRef is deprecated.
  // https://react.dev/reference/react/forwardRef
  elementRef: Ref<HTMLVdsBasicDropdownMultiselectElement>;
  items: DropdownItemProps<T>[];
  onChange: (newValue: T[]) => void;
}

// Support either a redux action or a callback function to handle the value change event.
interface UseDropdownMultiselectProps<T> {
  useItemsHook: () => DropdownItemProps<T>[];
  action?: (payload: T[]) => Action;
  callback?: (payload: T[]) => void;
}

export const useDropdownMultiselect = <T>({
  useItemsHook,
  action,
  callback,
}: UseDropdownMultiselectProps<T>): ReturnControlProps<T> => {
  const dispatch = useAppDispatch();

  const elementRef = useRef<HTMLVdsBasicDropdownMultiselectElement>(null);

  const items = useItemsHook();

  const onChange = useCallback(
    (newValue: T[]) => {
      if (action) {
        dispatch(action(newValue));
      } else if (callback) {
        callback(newValue);
      }
    },
    [dispatch, action, callback],
  );

  return { elementRef, items, onChange };
};

export default useDropdownMultiselect;
